import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { take } from 'rxjs';
import { map } from 'rxjs/operators';

import { TranslatePipe } from '@core/i18n-legacy';
import { CreateWithAiButtonComponent } from '@core/offers-ui';
import { htmlToJson, RtSchema } from '@core/prosemirror-legacy';
import { RenderDirective } from '@core/prosemirror-legacy/angular';
import {
  ProsemirrorPositionDescriptionConfig,
  ProsemirrorPositionDescriptionConfigDirective,
  WysiwygComponent,
} from '@core/prosemirror-legacy/webapp';
import { CompanyVerificationDataService } from '@core/tellent-company-verification-logic';
import { CompanyVerificationRequestStatus } from '@core/tellent-company-verification-types';
import { TellentAccessService, TellentAppsDataFacade } from '@core/tellent-logic';
import { TellentPositionsDataFacade } from '@core/tellent-positions-logic';
import { TellentPosition } from '@core/tellent-positions-types';
import { SeatKind } from '@core/tellent-types';
import {
  AiLogoComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  EmptyStateComponent,
  IconComponent,
  ProtectedControlDirective,
} from '@core/user-interface-legacy';

import { PositionDescriptionInsertAiMessageComponent } from './position-description-insert-ai-message/position-description-insert-ai-message.component';

export enum DescriptionMode {
  Edit = 'edit',
  View = 'view',
}

@Component({
  selector: 'tlc-position-details-description',
  templateUrl: './position-details-description.component.html',
  styleUrls: ['./position-details-description.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AiLogoComponent,
    ButtonComponent,
    CreateWithAiButtonComponent,
    EmptyStateComponent,
    IconComponent,
    NgTemplateOutlet,
    PositionDescriptionInsertAiMessageComponent,
    ProtectedControlDirective,
    ProsemirrorPositionDescriptionConfigDirective,
    ReactiveFormsModule,
    RenderDirective,
    TranslatePipe,
    WysiwygComponent,
    ButtonLoadingDirective,
  ],
})
export class PositionDetailsDescriptionComponent {
  private readonly companyVerificationService = inject(CompanyVerificationDataService);
  private readonly positionsDataFacade = inject(TellentPositionsDataFacade);

  private readonly aiFooterTemplate = viewChild.required<TemplateRef<any>>('aiFooterTemplate');

  public readonly canManageUsers = inject(TellentAccessService).canManageTellentUsers;
  public readonly hasPaidCoreHrSubscription = inject(
    TellentAppsDataFacade,
  ).hasAnySubscriptionStatus(SeatKind.CoreHR, ['active', 'non_renewing']);

  public readonly positionData = input.required<TellentPosition | null>();
  public readonly isAccountVerified = toSignal(
    this.companyVerificationService.status$.pipe(
      map(status => status === CompanyVerificationRequestStatus.Approved),
    ),
    { initialValue: false },
  );

  public get footerTemplate(): TemplateRef<any> {
    return this.aiFooterTemplate();
  }

  public get descriptionJsonValue(): object | null {
    return <object | null>this.descriptionJson.value;
  }

  public readonly ProsemirrorPositionDescriptionConfig = ProsemirrorPositionDescriptionConfig;
  public readonly descriptionJson = new FormControl<unknown>(null);
  public readonly descriptionJsonSignal = toSignal(this.descriptionJson.valueChanges, {
    initialValue: this.descriptionJson.value,
  });
  public readonly hasDescriptionContent = computed(() => !!this.descriptionJsonSignal());
  public readonly mode = signal<DescriptionMode | null>(null);
  public readonly aiIsLoading = signal(false);
  public readonly showAiPanel = signal(false);
  public readonly loading = signal(false);

  public readonly showEditor = computed(() => this.mode() === DescriptionMode.Edit);

  public readonly showContent = computed(
    () =>
      this.mode() === DescriptionMode.View ||
      (!this.mode() && !!this.positionData()?.descriptionJson),
  );

  constructor() {
    effect(() => {
      const position = this.positionData();
      if (position?.descriptionJson) {
        this.descriptionJson.setValue(position.descriptionJson);
        this.descriptionJson.markAsPristine();
        if (!this.mode()) {
          this.mode.set(DescriptionMode.View);
        }
      } else {
        this.descriptionJson.setValue(null);
        this.descriptionJson.markAsPristine();
        const currentMode = this.mode();
        if (currentMode && currentMode !== DescriptionMode.Edit) {
          this.mode.set(null);
        }
      }
    });
  }

  public onAddDescription(): void {
    this.mode.set(DescriptionMode.Edit);
  }

  public onEdit(): void {
    this.mode.set(DescriptionMode.Edit);
  }

  public onGenerateWithAi(): void {
    this.mode.set(DescriptionMode.Edit);
    this.showAiPanel.set(true);
    this.aiIsLoading.set(true);
    this.generateDescription();
  }

  public onWriteWithAi(): void {
    this.mode.set(DescriptionMode.Edit);
    this.showAiPanel.set(true);
    this.aiIsLoading.set(true);
    this.generateDescription();
  }

  public onSave(): void {
    const position = this.positionData();
    if (!position) {
      return;
    }

    this.loading.set(true);
    const guid = position.guid;
    const descriptionJson = this.descriptionJson.value;

    this.positionsDataFacade
      .updateDescription(guid, descriptionJson)
      .pipe(take(1))
      .subscribe(result => {
        this.loading.set(false);
        if (result?.isSuccess) {
          this.descriptionJson.markAsPristine();
          this.mode.set(descriptionJson ? DescriptionMode.View : null);
        }
      });
  }

  public onCancel(): void {
    const originalValue = this.positionData()?.descriptionJson ?? null;
    this.descriptionJson.setValue(originalValue);
    this.descriptionJson.markAsPristine();
    this.mode.set(originalValue ? DescriptionMode.View : null);
  }

  private generateDescription(): void {
    const positionName = this.positionData()?.name ?? '';
    this.positionsDataFacade
      .generateDescription({ positionName })
      .pipe(take(1))
      .subscribe({
        next: response => {
          const contentJson = htmlToJson(response.descriptionHtml, RtSchema.PositionDescription);
          this.descriptionJson.setValue(contentJson);
          this.descriptionJson.markAsDirty();
          this.showAiPanel.set(false);
          this.aiIsLoading.set(false);
        },
        error: () => {
          this.showAiPanel.set(false);
          this.aiIsLoading.set(false);
        },
      });
  }
}
